import React, { useState, useEffect } from "react";
import {
  Slideout,
  Tabs,
  TextField,
  Typography,
  Button,
  Icon,
  Alert,
} from "@ann/web-components";
import {
  SlideoutContainer,
  SlideoutHeader,
  SlideoutSubHeader,
  SlideoutDrivesContainer,
  SlideoutActions,
  BackButtonWrapper,
  SlideoutContentWrapper,
} from "./SlideoutDrives.styled";
import { MyDrivePage, SharedWithMePage, CompanyDrivePage } from "./pages";
import { useStore } from "../../../../../../../hooks";
import { useDebounce } from "../../../../../../../hooks";
import { observer } from "mobx-react";
import { AddFilesConfirmationModal } from "../Modals";

const SlideoutDrives = observer(({ open, onClose }) => {
  const { knowledgeStore, authStore } = useStore();
  const [showAddConfirmationModal, setShowAddConfirmationModal] =
    useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  const debouncedSearchQuery = useDebounce(localSearchQuery);

  useEffect(() => {
    knowledgeStore.setSearchQuery('');
    setLocalSearchQuery('');
  }, [knowledgeStore.currentView]);

  const handleBackToList = () => {
    knowledgeStore.documentStore.setDocumentList([]);
    knowledgeStore.clearSelectedDrive();
    knowledgeStore.setSearchQuery('');
    setLocalSearchQuery('');
  };

  const handleAddConfirmationModal = () => {
    setShowAddConfirmationModal(true);
    onClose();
  };

  const handleCloseAddConfirmationModal = () => {
    knowledgeStore.clearDriveSelections();
    knowledgeStore.clearDocumentSelections();
    setShowAddConfirmationModal(false);
  };

  const handleSearchChange = (value) => {
    const searchValue = typeof value === 'string' ? value : (value?.target?.value || '');
    setLocalSearchQuery(searchValue);
  };

  const isLoading = knowledgeStore.agentStore.documentsLoading || knowledgeStore.agentStore.drivesLoading || knowledgeStore.agentStore.assignDocumentLoading || knowledgeStore.agentStore.assignVaultLoading;

  return (
    <>
      <Slideout onClose={onClose} size="medium" open={open}>
        {knowledgeStore.isDocumentsView && (
          <Slideout.HeaderActions>
            <BackButtonWrapper>
              <Icon name="arrowLeft" size="large" onClick={handleBackToList} />
            </BackButtonWrapper>
          </Slideout.HeaderActions>
        )}
        <SlideoutContainer>
          <SlideoutHeader>
            <SlideoutSubHeader>
              <Typography category="title" size="large" weight="bold">
                {knowledgeStore.selectedDriveTitle}
              </Typography>
            </SlideoutSubHeader>
            <TextField
              onChange={handleSearchChange}
              size="medium"
              iconLeft="search"
              placeholder={knowledgeStore.isDocumentsView ? "Search files" : "Search drives"}
              value={localSearchQuery}
            />
          </SlideoutHeader>
          <SlideoutDrivesContainer>
            <SlideoutContentWrapper>
            <Tabs defaultIndex={0} size="medium">
              <Tabs.List>
                <Tabs.Tab index={0} size="small">
                  My Drive
                </Tabs.Tab>
                <Tabs.Tab index={1} size="small" disabled>
                  Shared With Me
                </Tabs.Tab>
                <Tabs.Tab index={2} size="small" disabled>
                  Company Drive
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel index={0}>
                <MyDrivePage searchQuery={debouncedSearchQuery} />
              </Tabs.Panel>
              <Tabs.Panel index={1}>
                <SharedWithMePage />
              </Tabs.Panel>
              <Tabs.Panel index={2}>
                <CompanyDrivePage />
              </Tabs.Panel>
            </Tabs>
            </SlideoutContentWrapper>
          </SlideoutDrivesContainer>
          <SlideoutActions>
            <Button
              label={`Grant Access (${knowledgeStore.selectedDrivesCount + knowledgeStore.selectedDocumentsCount})`}
              size="medium"
              type="primary"
              onClick={handleAddConfirmationModal}
            />
          </SlideoutActions>
        </SlideoutContainer>
      </Slideout>
      <AddFilesConfirmationModal
        isOpen={showAddConfirmationModal}
        onClose={handleCloseAddConfirmationModal}
        selectedDrives={knowledgeStore.vaultStore.vaultList?.data?.filter(vault =>
          knowledgeStore.selectedDrives?.includes(vault.vaultUuid)
        ) || []}
        selectedDocuments={knowledgeStore.documentStore.documentList?.data?.filter(document =>
          knowledgeStore.selectedDocuments?.includes(document.uuid)
        ) || []}
        isLoading={isLoading}
      />
      {knowledgeStore.alert.show && (
        <Alert
          description={knowledgeStore.alert.description}
          severity={knowledgeStore.alert.severity}
          maxWidth="500px"
          closable={true}
          autoClose={knowledgeStore.alert.autoClose}
          autoCloseDelay={knowledgeStore.alert.autoCloseDelay}
          onClose={() => knowledgeStore.hideAlert()}
        />
      )}
    </>
  );
});

export default SlideoutDrives;
